// elois-correa-match.js
// Event-driven sync for harness + free leash bundle.

(() => {
  const CORREA_VARIANT_MAP = {
    Negro: 53927818133841,
    Azul: 53927818232145,
    Naranja: 53927818264913,
    Rojo: 53927818166609,
    Rosa: 53927818199377,
  };
  const GUIA_CONECTA_VARIANT_ID = 54007924425041;
  // Pack Completo Elois CalmWalk™: ya incluye la guía y los extras, así que su
  // presencia en el carrito desactiva la Guía Conecta gratuita automática.
  const PACK_COMPLETO_VARIANT_ID = 54028544213329;

  // Descripción de lo que incluye la Guía Conecta, inyectada en su fila del carrito.
  const GUIA_EXTRAS_HTML = `
    <div class="elois-guide-extras">
      </div>
      <div class="elois-guide-extra">
        <span class="elois-guide-check">✓</span>
        <div class="elois-guide-extra-text">
          <strong>Ebook “Conecta con tu Compañero”</strong>
          <span>Valorado en 20€</span>
        </div>
      </div>
      <div class="elois-guide-extra">
        <span class="elois-guide-check">✓</span>
        <div class="elois-guide-extra-text">
          <strong>Lámina Visual “Lenguaje No Verbal Canino”</strong>
          <span>Valorada en 10€</span>
        </div>
      </div>
      <div class="elois-guide-extra">
        <span class="elois-guide-check">✓</span>
        <div class="elois-guide-extra-text">
          <strong>Plantilla imprimible rellenable “Conoce Mejor a tu Perro”</strong>
          <span>Valorada en 5€</span>
        </div>
      </div>
    </div>
  `;

  const BUNDLE_FILTER = {
    handles: ['elois-calmwalk'],
    productTypes: [],
    productIds: [],
  };

  const CORREA_ID_TO_COLOR = Object.entries(CORREA_VARIANT_MAP).reduce((acc, [color, id]) => {
    acc[Number(id)] = color;
    return acc;
  }, {});

  const COLOR_KEYWORDS = ['color', 'colour', 'couleur', 'farbe', 'cor'];
  // Use the native fetch captured before content_for_header injects shop_events_listener.
  // This ensures our internal cart/add.js and cart/update.js calls are invisible to
  // Shopify's analytics wrapper, preventing false AddToCart events in Meta tracking
  // and the "Payload body and response have different number of items" warning.
  const NATIVE_FETCH = (window.__eloisNativeFetch || window.fetch).bind(window);
  const variantColorCache = new Map();

  let reconcileChain = Promise.resolve();
  let reconcileTimer = null;
  let reorderTimer = null;
  let pendingHide = false;
  let loadingActive = false;
  let isRefreshingUi = false;
  let initialized = false;

  function normalize(value) {
    if (!value) return '';
    return String(value)
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  function mapColor(value) {
    const normalized = normalize(value);
    return Object.keys(CORREA_VARIANT_MAP).find((color) => normalize(color) === normalized) || '';
  }

  function isCorrea(variantId) {
    return Boolean(CORREA_ID_TO_COLOR[Number(variantId)]);
  }

  function isGuiaConecta(variantId) {
    return Number(variantId) === GUIA_CONECTA_VARIANT_ID;
  }

  function isPackCompleto(variantId) {
    return Number(variantId) === PACK_COMPLETO_VARIANT_ID;
  }

  function isTaggedBundleLeash(item) {
    return item?.properties?._elois_bundle === '1' && item?.properties?._elois_bundle_role === 'leash';
  }

  function isTaggedBundleGuide(item) {
    return item?.properties?._elois_bundle === '1' && item?.properties?._elois_bundle_role === 'guide';
  }

  function getOrCreateOverlay() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return null;
    let overlay = cartDrawer.querySelector(':scope > .elois-cart-loading');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'elois-cart-loading';
      cartDrawer.appendChild(overlay);
    }
    return overlay;
  }

  function showCartLoading() {
    loadingActive = true;
    const overlay = getOrCreateOverlay();
    if (overlay) overlay.classList.add('is-visible');
  }

  function hideCartLoading() {
    loadingActive = false;
    const overlay = document.querySelector('cart-drawer > .elois-cart-loading');
    if (overlay) overlay.classList.remove('is-visible');
  }

  function isEligibleForBundle(item) {
    const { handles, productTypes, productIds } = BUNDLE_FILTER;
    if (!handles.length && !productTypes.length && !productIds.length) return true;
    if (handles.length && handles.includes(item.handle)) return true;
    if (productTypes.length && productTypes.includes(item.product_type)) return true;
    if (productIds.length && productIds.includes(Number(item.product_id))) return true;
    return false;
  }

  function colorFromItem(item) {
    if (!item) return '';

    const colorOption = (item.options_with_values || []).find((option) =>
      COLOR_KEYWORDS.some((keyword) => normalize(option.name).includes(keyword))
    );
    if (colorOption) {
      const mapped = mapColor(colorOption.value);
      if (mapped) return mapped;
    }

    for (const option of item.options_with_values || []) {
      const mapped = mapColor(option.value);
      if (mapped) return mapped;
    }

    if (item.variant_title) {
      for (const chunk of String(item.variant_title).split('/')) {
        const mapped = mapColor(chunk);
        if (mapped) return mapped;
      }
    }

    return '';
  }

  async function fetchCart() {
     const response = await NATIVE_FETCH('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`/cart.js error ${response.status}`);
    return response.json();
  }

  async function fetchVariantColor(variantId) {
    const id = Number(variantId);
    if (!id) return '';
    if (variantColorCache.has(id)) return variantColorCache.get(id);

    try {
      const response = await NATIVE_FETCH(`/variants/${id}.js`, { credentials: 'same-origin' });
      if (!response.ok) {
        variantColorCache.set(id, '');
        return '';
      }

      const variant = await response.json();
      const color = mapColor(variant.option1) || mapColor(variant.option2) || mapColor(variant.option3);
      variantColorCache.set(id, color);
      return color;
    } catch {
      variantColorCache.set(id, '');
      return '';
    }
  }

  async function doReconcile() {
    const cart = await fetchCart();
    const harnessByColor = {};
    const taggedLeashesByColor = {};
    const taggedGuideLines = [];
    let hasEligibleHarness = false;
    let hasPackCompleto = false;
    const updates = {};

    for (const item of cart.items || []) {
      const variantId = Number(item.variant_id);

      if (isPackCompleto(variantId)) {
        hasPackCompleto = true;
        continue;
      }

      if (isGuiaConecta(variantId)) {
        if (isTaggedBundleGuide(item)) taggedGuideLines.push(item);
        continue;
      }

      if (isCorrea(variantId)) {
        if (!isTaggedBundleLeash(item)) {
          // Remove stray leash lines that were added by other scripts without bundle tagging.
          updates[item.key] = 0;
          continue;
        }

        const taggedColor = mapColor(item?.properties?._elois_bundle_color) || CORREA_ID_TO_COLOR[variantId];
        if (!taggedColor) {
          updates[item.key] = 0;
          continue;
        }

        if (!taggedLeashesByColor[taggedColor]) taggedLeashesByColor[taggedColor] = [];
        taggedLeashesByColor[taggedColor].push(item);
        continue;
      }

      if (!isEligibleForBundle(item)) continue;
      const color = colorFromItem(item) || (await fetchVariantColor(variantId));
      if (!color || !CORREA_VARIANT_MAP[color]) continue;
      hasEligibleHarness = true;
      harnessByColor[color] = (harnessByColor[color] || 0) + Number(item.quantity || 0);
    }

    const additions = [];

    for (const [color, leashVariantId] of Object.entries(CORREA_VARIANT_MAP)) {
      const targetQuantity = harnessByColor[color] || 0;
      const lines = taggedLeashesByColor[color] || [];

      if (!lines.length) {
        if (targetQuantity > 0) {
          additions.push({
            id: Number(leashVariantId),
            quantity: targetQuantity,
            properties: {
              _elois_bundle: '1',
              _elois_bundle_role: 'leash',
              _elois_bundle_color: color,
              _elois_bundle_source: 'elois-correa-match-v3',
            },
          });
        }
        continue;
      }

      updates[lines[0].key] = targetQuantity;
      for (let index = 1; index < lines.length; index += 1) {
        updates[lines[index].key] = 0;
      }
    }

    if (hasEligibleHarness && !hasPackCompleto) {
      if (taggedGuideLines.length) {
        updates[taggedGuideLines[0].key] = 1;
        for (let index = 1; index < taggedGuideLines.length; index += 1) {
          updates[taggedGuideLines[index].key] = 0;
        }
      } else {
        additions.push({
          id: GUIA_CONECTA_VARIANT_ID,
          quantity: 1,
          properties: {
            _elois_bundle: '1',
            _elois_bundle_role: 'guide',
            _elois_bundle_source: 'elois-correa-match-v3',
          },
        });
      }
    } else {
      // Sin arnés elegible (se borraron todos) o con el Pack Completo presente:
      // la Guía Conecta automática no debe existir → eliminamos cualquier línea de guía.
      for (const line of taggedGuideLines) {
        updates[line.key] = 0;
      }
    }

    let changed = false;

    if (Object.keys(updates).length) {
      await NATIVE_FETCH('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      changed = true;
    }

    if (additions.length) {
      await NATIVE_FETCH('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: additions }),
      });
      changed = true;
    }

    return changed;
  }

  function getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : html;
  }

  async function refreshCartIconBubble() {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    try {
        const response = await NATIVE_FETCH('/cart?sections=cart-icon-bubble', {
        credentials: 'same-origin',
      });
      if (!response.ok) return;
      const sections = await response.json();
      if (!sections['cart-icon-bubble']) return;
      bubble.innerHTML = getSectionInnerHTML(sections['cart-icon-bubble']);
    } catch {
      // Non-fatal UI refresh failure.
    }
  }

  async function refreshCartUi() {
    if (isRefreshingUi) return;
    isRefreshingUi = true;

    try {
      const updates = [];

      if (document.querySelector('cart-drawer-items') || document.querySelector('.cart-drawer__footer')) {
        updates.push(
          (async () => {
            const response = await NATIVE_FETCH('/cart?section_id=cart-drawer', {
              credentials: 'same-origin',
            });
            if (!response.ok) return;
            const htmlText = await response.text();
            const html = new DOMParser().parseFromString(htmlText, 'text/html');

            ['cart-drawer-items', '.drawer__footer'].forEach((selector) => {
              const target = document.querySelector(selector);
              const source = html.querySelector(selector);
              if (target && source) target.replaceWith(source);
            });
          })()
        );
      }

      const mainCartRoot = document.getElementById('main-cart-items');
      if (mainCartRoot) {
        updates.push(
          (async () => {
            const response = await NATIVE_FETCH('/cart?section_id=main-cart-items', {
              credentials: 'same-origin',
            });
            if (!response.ok) return;
            const htmlText = await response.text();
            const html = new DOMParser().parseFromString(htmlText, 'text/html');
            const sourceCartItems = html.querySelector('cart-items');
            const targetContents = mainCartRoot.querySelector('.js-contents');
            if (sourceCartItems && targetContents) {
              targetContents.innerHTML = sourceCartItems.innerHTML;
            }
          })()
        );
      }

      updates.push(refreshCartIconBubble());
      await Promise.all(updates);
    } finally {
      isRefreshingUi = false;
    }
  }

  function scheduleReorder(hideWhenDone = false) {
    if (reorderTimer) clearTimeout(reorderTimer);
    if (hideWhenDone) pendingHide = true;
    reorderTimer = setTimeout(() => {
      reorderTimer = null;
      doReorder().catch((error) => {
        console.error('[ELOIS] Reorder failed:', error);
        if (pendingHide || loadingActive) {
          pendingHide = false;
          hideCartLoading();
        }
      });
    }, 180);
  }

  async function doReorder() {
    const shouldHide = pendingHide;
    pendingHide = false;

    const cart = await fetchCart();
    const lineInfoByKey = {};

    for (const item of cart.items || []) {
      const variantId = Number(item.variant_id);

      if (isGuiaConecta(variantId) && isTaggedBundleGuide(item)) {
        lineInfoByKey[item.key] = { type: 'guide', color: '' };
        continue;
      }

      if (isCorrea(variantId) && isTaggedBundleLeash(item)) {
        const color = mapColor(item?.properties?._elois_bundle_color) || CORREA_ID_TO_COLOR[variantId] || '';
        lineInfoByKey[item.key] = { type: 'correa', color };
        continue;
      }

      if (!isCorrea(variantId) && isEligibleForBundle(item)) {
        const color = colorFromItem(item) || (await fetchVariantColor(variantId));
        lineInfoByKey[item.key] = { type: 'arnes', color: color || '' };
      }
    }

    const tbodies = document.querySelectorAll('#main-cart-items .cart-items tbody, #CartDrawer-CartItems .cart-items tbody');

    tbodies.forEach((tbody) => {
      if (!tbody || !tbody.isConnected) return;

      const rows = Array.from(tbody.querySelectorAll(':scope > .cart-item'));
      if (rows.length < 2) return;

      const rowData = rows.map((row) => {
        const key = row.dataset.eloisLineKey || '';
        const info = lineInfoByKey[key] || { type: 'other', color: '' };
        return { row, info };
      });

      const anchors = {};
      rowData.forEach(({ row, info }) => {
        if (info.type === 'arnes' && info.color) anchors[info.color] = row;
      });

      rowData.forEach(({ row, info }) => {
        if (info.type === 'guide') {
          row.classList.add('elois-matching-row');

          // Cantidad no editable/clicable: mismo tratamiento que las correas.
          const quantityCell = row.querySelector('.cart-item__quantity');
          const quantityInput = row.querySelector('.quantity__input');
          if (quantityCell && quantityInput) {
            quantityCell.innerHTML = `<span class="quantity__">${quantityInput.value.trim()} Unidades GRATIS!!</span>`;
          }

          // Descripción de los extras incluidos (se inyecta una sola vez por render).
          if (!row.querySelector('.elois-guide-extras')) {
            const name = row.querySelector('.cart-item__name');
            if (name) name.insertAdjacentHTML('afterend', GUIA_EXTRAS_HTML);
          }
          return;
        }

        if (info.type !== 'correa' || !info.color) return;

        const anchor = anchors[info.color];
        if (!anchor || !anchor.isConnected || !row.isConnected || !tbody.isConnected) return;

        row.classList.add('elois-matching-row');
        const quantityCell = row.querySelector('.cart-item__quantity');
        const quantityInput = row.querySelector('.quantity__input');
        if (quantityCell && quantityInput) {
          quantityCell.innerHTML = `<span class="quantity__">${quantityInput.value.trim()} Unidades GRATIS!!</span>`;
        }

        if (anchor.nextSibling !== row) {
          tbody.insertBefore(row, anchor.nextSibling);
        }
        anchors[info.color] = row;
      });

      // La Guía Conecta siempre se coloca como último elemento del carrito.
      rowData.forEach(({ row, info }) => {
        if (info.type !== 'guide') return;
        if (row.isConnected && tbody.isConnected && tbody.lastElementChild !== row) {
          tbody.appendChild(row);
        }
      });
    });

    if (shouldHide) hideCartLoading();
  }

  function enqueueReconcile(reason) {
    reconcileChain = reconcileChain
      .then(async () => {
        showCartLoading();
        const changed = await doReconcile();
        if (changed) await refreshCartUi();
        scheduleReorder(true);
      })
      .catch((error) => {
        console.error('[ELOIS] Reconcile failed:', reason, error);
        hideCartLoading();
      });

    return reconcileChain;
  }

  function scheduleReconcile(reason) {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    showCartLoading();
    reconcileTimer = setTimeout(() => {
      reconcileTimer = null;
      enqueueReconcile(reason);
    }, 120);
  }

  function setupObservers() {
    ['CartDrawer', 'main-cart-items'].forEach((id) => {
      const root = document.getElementById(id);
      if (!root || root._eloisObserved) return;

      root._eloisObserved = true;
      new MutationObserver((mutations) => {
        const hasRealChange = mutations.some((mutation) =>
          Array.from(mutation.addedNodes)
            .concat(Array.from(mutation.removedNodes))
            .some((node) => !(node.nodeType === 1 && node.classList && node.classList.contains('elois-cart-loading')))
        );
        if (hasRealChange) scheduleReorder(false);
      }).observe(root, { childList: true, subtree: true });
    });
  }

  function setupHooks() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'elois-bundle') return;
        scheduleReconcile('pubsub-cartUpdate');
      });
    }

    document.addEventListener('cart:updated', () => {
      scheduleReconcile('dom-cart:updated');
    });

    document.addEventListener('cart:open', () => {
      scheduleReconcile('dom-cart:open');
    });

    setupObservers();

    if (!document.getElementById('CartDrawer') || !document.getElementById('main-cart-items')) {
      const watchBody = new MutationObserver(() => {
        setupObservers();
        if (document.getElementById('CartDrawer') && document.getElementById('main-cart-items')) {
          watchBody.disconnect();
        }
      });
      watchBody.observe(document.body, { childList: true, subtree: true });
    }
  }

  function bootstrap() {
    if (initialized) return;
    initialized = true;
    setupHooks();
    scheduleReconcile('init');
    console.log('[ELOIS] Correa Match v3 active:', window.location.pathname);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }

  window.EloisCart = Object.assign({}, window.EloisCart, {
    reconcileNow: () => enqueueReconcile('manual'),
    showCartLoading,
    hideCartLoading,
  });
})();
